import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useSearchParams, Link } from "react-router-dom";
import { useGoBack } from "../../lib/useGoBack";
import {
  AlertCircle,
  ArrowLeft,
  ArrowRight,
  Building2,
  Check,
  CheckCheck,
  CheckCircle2,
  Clock,
  Copy,
  ExternalLink,
  GraduationCap,
  Link as LinkIcon,
  Loader2,
  Lock,
  ShieldCheck,
  Smartphone,
  Sparkles,
  Star,
  Zap,
} from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { useStudent } from "../../context/StudentContext";
import { api, ApiError, type Entitlement, type PremiumPackage, type SemesterStatus } from "../../lib/api";
import { mapApiError } from "../../components/ui/StatusMessage";
import { saveRedirectTarget } from "../../lib/redirect";
import { invalidateStudySubjectsCache } from "../../lib/useStudySubjects";
import { StatusBanner } from "../../components/ui/StatusBanner";

interface PendingPaymentInfo {
  id: string;
  txRef: string;
  txnId: string;
  plan: string;
  amount: number;
  amountEtb: number;
  date: string | null;
  status: string;
  semester?: number;
  stream?: string;
  packageId?: string | null;
  packageName?: string | null;
  provider?: string;
  isChapa?: boolean;
  canCancel?: boolean;
  checkoutUrl?: string | null;
}

export function PremiumPage() {
  const { tokens, isAuthenticated } = useAuth();
  const { setIsPremium, isAdminPreview } = useStudent();
  const token = tokens?.accessToken ?? null;
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const leavePremium = useGoBack("/student/study");

  // Admin stream preview state for testing packages and content across streams
  const [adminSelectedStream, setAdminSelectedStream] = useState<"natural" | "social">("natural");

  // Authentication guard: Logged-out users must go to login with redirect parameter
  useEffect(() => {
    if (!isAuthenticated) {
      const fullTarget = location.pathname + location.search;
      saveRedirectTarget(fullTarget);
      navigate(`/auth/login?redirect=${encodeURIComponent(fullTarget)}`, { replace: true });
    }
  }, [isAuthenticated, location.pathname, location.search, navigate]);

  // Packages & status state
  const [packages, setPackages] = useState<PremiumPackage[]>([]);
  const [entitlements, setEntitlements] = useState<Entitlement[]>([]);
  const [semesterStatuses, setSemesterStatuses] = useState<SemesterStatus[]>([]);
  const [loadingData, setLoadingData] = useState(true);
  const [pendingLoaded, setPendingLoaded] = useState(false);

  // Student profile state (read-only stream from profile)
  const [studentProfile, setStudentProfile] = useState<{
    fullName: string;
    streamId?: string | null;
    streamName?: string | null;
    streamCode?: string | null;
    currentSemester?: number;
    canChangeStream?: boolean;
  } | null>(null);

  // Selected semester tab (1, 2, "bundle", or any active semester)
  const urlSemParam = searchParams.get("semester");
  const initialSemester = useMemo(() => {
    if (urlSemParam === "bundle") return "bundle";
    if (urlSemParam) {
      const parsed = parseInt(urlSemParam, 10);
      if (!isNaN(parsed) && parsed > 0) return parsed;
    }
    return 1;
  }, [urlSemParam]);
  const [selectedSemesterTab, setSelectedSemesterTab] = useState<number | "bundle">(initialSemester);

  // Step state: 2-step unified flow (1: Select Package, 2: Fast Payment)
  const stepParam = searchParams.get("step");
  const currentStep: 1 | 2 = useMemo(() => {
    if (stepParam === "2" || stepParam === "3") return 2;
    return 1;
  }, [stepParam]);

  // Step 2 is a real history entry (tagged), so browser/Telegram back returns to step 1 first.
  // Everything else on this page replaces, so leaving the page from step 1 is a single back.
  const goToStep = (step: 1 | 2, force = false) => {
    if (step === 2 && !force && !canProceed) return;
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set("step", String(step));
        return next;
      },
      step === 2 && currentStep === 1 && !force ? { state: { fromStep1: true } } : { replace: true },
    );
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const backToPackages = () => {
    if ((location.state as { fromStep1?: boolean } | null)?.fromStep1) navigate(-1);
    else goToStep(1);
  };

  // Selected package
  const [selectedPackageId, setSelectedPackageId] = useState<string | null>(null);

  // Payment method & submission state
  const [plan, setPlan] = useState<"monthly" | "semester">("semester");
  const [paymentMode, setPaymentMode] = useState<"manual" | "online">("manual");
  const [manualMethod, setManualMethod] = useState<"telebirr" | "cbe">("telebirr");
  const [txnId, setTxnId] = useState("");
  const [cbeReceiptLink, setCbeReceiptLink] = useState("");
  const [verifyingManual, setVerifyingManual] = useState(false);
  const [verifyingCbe, setVerifyingCbe] = useState(false);
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [altLoading, setAltLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [messageTone, setMessageTone] = useState<"info" | "error" | "success">("info");
  const [pendingPayment, setPendingPayment] = useState<PendingPaymentInfo | null>(null);
  const [cancellingManual, setCancellingManual] = useState(false);
  const [submittedReviewInfo, setSubmittedReviewInfo] = useState<{ txnId: string; plan: string; amount: number } | null>(null);
  const [paymentSuccess, setPaymentSuccess] = useState(false);

  // Client idempotency key: distinct per package & semester choice, stable across retries/double-clicks
  const idempotencyKey = useMemo(
    () => `prem-${selectedSemesterTab}-${selectedPackageId || "default"}-${Date.now()}-${Math.random().toString(36).substring(2, 7)}`,
    [selectedSemesterTab, selectedPackageId],
  );

  // Fallback plans metadata for payment gateways configuration
  const [legacyPlans, setLegacyPlans] = useState<{
    chapaConfigured?: boolean;
    telebirrConfigured?: boolean;
    chapaEnabled?: boolean;
    telebirrEnabled?: boolean;
    telebirrManualPhone?: string;
    telebirrManualName?: string;
    telebirrManualEnabled?: boolean;
    cbeAccountNumber?: string;
    cbeAccountName?: string;
    cbeReceiveLink?: string;
    cbeManualEnabled?: boolean;
    manualInstructions?: string;
    manualPaymentEnabled?: boolean;
    fastestPaymentMethod?: string;
    expressCheckoutEnabled?: boolean;
  } | null>(null);

  // Load packages, user profile stream, entitlements, and semester status
  useEffect(() => {
    let cancelled = false;
    setLoadingData(true);

    const load = async () => {
      try {
        const [pkgs, plansRes] = await Promise.all([
          api.payments.packages().catch(() => [] as PremiumPackage[]),
          api.payments.plans().catch(() => null),
        ]);

        if (cancelled) return;
        setPackages(pkgs);
        if (plansRes) {
          setLegacyPlans(plansRes);
          if (plansRes.expressCheckoutEnabled) {
            const fMethod = plansRes.fastestPaymentMethod || "telebirr_direct";
            if (fMethod === "chapa" || fMethod === "telebirr_h5") {
              setPaymentMode("online");
            } else if (fMethod === "cbe_birr") {
              setPaymentMode("manual");
              setManualMethod("cbe");
            } else {
              setPaymentMode("manual");
              setManualMethod("telebirr");
            }
          }
        }

        if (token) {
          const [ents, semStats, prof] = await Promise.all([
            api.payments.entitlements(token).catch(() => [] as Entitlement[]),
            api.payments.semesterStatus(token).catch(() => [] as SemesterStatus[]),
            api.profile(token).catch(() => null),
          ]);

          if (cancelled) return;
          setEntitlements(ents);
          setSemesterStatuses(semStats);
          if (prof) {
            setStudentProfile(prof);
          }
        }
      } finally {
        if (!cancelled) setLoadingData(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [token]);

  // Check for pending request for currently selected semester
  const semQuery = typeof selectedSemesterTab === "number" ? selectedSemesterTab : undefined;
  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    api.payments
      .getPending(token, semQuery)
      .then((res) => {
        if (cancelled) return;
        if (res.hasPending && res.payment) {
          setPendingPayment(res.payment);
        } else {
          setPendingPayment(null);
        }
        setPendingLoaded(true);
      })
      .catch(() => {
        if (!cancelled) {
          setPendingPayment(null);
          setPendingLoaded(true);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [token, semQuery]);

  // If user has a pending payment and no explicit step in URL, default to step 2 so they see it
  useEffect(() => {
    if (pendingPayment && !stepParam) {
      goToStep(2, true);
    }
  }, [pendingPayment, stepParam]);

  // Determine available payment options
  const chapaAvailable = Boolean(legacyPlans?.chapaConfigured && legacyPlans?.chapaEnabled !== false);
  const telebirrGatewayAvailable = Boolean(legacyPlans?.telebirrConfigured && legacyPlans?.telebirrEnabled !== false);
  const onlineAvailable = chapaAvailable || telebirrGatewayAvailable;

  const telebirrManualAvailable = Boolean(
    (legacyPlans?.manualPaymentEnabled ?? true) &&
    legacyPlans?.telebirrManualEnabled !== false
  );
  const cbeManualAvailable = Boolean(
    (legacyPlans?.manualPaymentEnabled ?? true) &&
    legacyPlans?.cbeManualEnabled !== false
  );
  const manualAvailable = telebirrManualAvailable || cbeManualAvailable;

  // Read stream strictly from profile (or admin preview stream)
  const hasProfileStream = Boolean(
    isAdminPreview ||
    studentProfile?.streamId ||
    (studentProfile?.streamName && studentProfile.streamName.toLowerCase() !== "common") ||
    (studentProfile?.streamCode && studentProfile.streamCode.toLowerCase() !== "common")
  );

  const profileStreamCode = useMemo(() => {
    if (isAdminPreview) return adminSelectedStream;
    if (studentProfile?.streamCode) return studentProfile.streamCode.toLowerCase();
    if (studentProfile?.streamName) {
      const lower = studentProfile.streamName.toLowerCase();
      if (lower.includes("natural")) return "natural";
      if (lower.includes("social")) return "social";
    }
    return "common";
  }, [studentProfile, isAdminPreview, adminSelectedStream]);

  // Discover all distinct semesters from catalog packages and status records
  const availableSemesters = useMemo(() => {
    const semSet = new Set<number>([1, 2]);
    packages.forEach((pkg) => {
      const sems = pkg.semesters_covered || pkg.semestersCovered || [];
      sems.forEach((s) => {
        if (typeof s === "number" && s > 0) semSet.add(s);
      });
    });
    semesterStatuses.forEach((s) => {
      if (typeof s.semester === "number" && s.semester > 0) semSet.add(s.semester);
    });
    return Array.from(semSet).sort((a, b) => a - b);
  }, [packages, semesterStatuses]);

  // Semester tabs for selection interface
  const semesterTabs = useMemo(() => {
    const tabs: Array<{ id: number | "bundle"; label: string; subtext: string; detail: string }> = availableSemesters.map((s) => {
      const semStatusObj = semesterStatuses.find((stat) => stat.semester === s);
      const isFullyPrem = Boolean(semStatusObj?.is_fully_premium || semStatusObj?.isFullyPremium);
      return {
        id: s,
        label: `Semester ${s}`,
        subtext: s === 1 ? "Freshman Sem I" : s === 2 ? "Freshman Sem II" : isFullyPrem ? "Premium Semester" : `University Sem ${s}`,
        detail: s === 1
          ? "General foundation courses & mid/final exams"
          : s === 2
          ? "Discipline-specific stream courses & past exams"
          : "Full premium curriculum, module notes, and exam practice",
      };
    });

    tabs.push({
      id: "bundle",
      label: "Full Year Bundle",
      subtext: "Semesters 1 & 2",
      detail: "Complete first-year university coverage with maximum savings",
    });

    return tabs;
  }, [availableSemesters, semesterStatuses]);

  // Filter packages based on selected semester tab and profile stream
  // FIX: Prevent full bundle from leaking into single-semester tabs
  const semesterComingSoon = useMemo(
    () =>
      typeof selectedSemesterTab === "number" &&
      semesterStatuses.some((s) => s.semester === selectedSemesterTab && (s.status === "coming_soon" || s.isAvailable === false)),
    [semesterStatuses, selectedSemesterTab],
  );
  const filteredPackages = useMemo(() => {
    if (semesterComingSoon) return [];
    return packages.filter((pkg) => {
      if (pkg.is_active === false || pkg.isActive === false) return false;
      // Semester filter
      const sems = pkg.semesters_covered || pkg.semestersCovered || [];
      if (selectedSemesterTab === "bundle") {
        if (sems.length <= 1) return false;
      } else {
        // Prevent multi-semester bundle from leaking into single semester tabs
        if (sems.length > 1) return false;
        if (!sems.includes(selectedSemesterTab as number)) return false;
      }

      // Stream filter: "common" packages match any stream; stream-specific match profile stream
      if (pkg.stream && pkg.stream !== "common") {
        if (profileStreamCode && profileStreamCode !== "common" && pkg.stream !== profileStreamCode) {
          return false;
        }
      }

      return true;
    });
  }, [packages, selectedSemesterTab, profileStreamCode, semesterComingSoon]);

  // Automatically select the best package (recommended or lowest display order)
  useEffect(() => {
    if (filteredPackages.length > 0) {
      const exists = filteredPackages.some((p) => p.id === selectedPackageId);
      if (!exists) {
        const recommended = filteredPackages.find((p) => p.is_recommended);
        setSelectedPackageId((recommended || filteredPackages[0]).id);
      }
    } else {
      setSelectedPackageId(null);
    }
  }, [filteredPackages, selectedPackageId]);

  const selectedPackage = useMemo(() => {
    return filteredPackages.find((p) => p.id === selectedPackageId) || filteredPackages[0] || null;
  }, [filteredPackages, selectedPackageId]);

  // Only an available, selected package can open the verification step.
  const canProceed = !loadingData && Boolean(selectedPackage) && (hasProfileStream || isAdminPreview);

  // Opening step 2 without a valid package sends the student back to package selection.
  useEffect(() => {
    if (currentStep === 2 && !loadingData && pendingLoaded && !pendingPayment && !canProceed) {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set("step", "1");
          return next;
        },
        { replace: true },
      );
    }
  }, [currentStep, loadingData, pendingLoaded, pendingPayment, canProceed, setSearchParams]);

  // Set plan ("semester" vs "monthly") matching selected package
  useEffect(() => {
    if (selectedPackage) {
      setPlan(selectedPackage.plan_type || "semester");
    }
  }, [selectedPackage]);

  // Synchronize payment amounts
  const amount = selectedPackage ? selectedPackage.price : (plan === "monthly" ? 99 : 399);

  // Manual payment details
  const manualPhone = legacyPlans?.telebirrManualPhone || "0967344843";
  const manualName = legacyPlans?.telebirrManualName || "Atinaf";
  const cbeAccNumber = legacyPlans?.cbeAccountNumber || "1000665516999";
  const cbeAccName = legacyPlans?.cbeAccountName || "Soorgaa Education";
  const cbeSupportLink = legacyPlans?.cbeReceiveLink || "https://t.me/Soorgaa_bot";
  const customInstructions = legacyPlans?.manualInstructions;

  const isTelebirr = manualMethod === "telebirr";
  const manualReady = isTelebirr ? telebirrManualAvailable : cbeManualAvailable;

  const manualDetails = isTelebirr
    ? [
      { label: "Receiver name", value: manualName, copyKey: "" },
      { label: "Telebirr phone number", value: manualPhone, copyKey: "phone" },
    ]
    : [
      { label: "Account name", value: cbeAccName, copyKey: "" },
      { label: "CBE account number", value: cbeAccNumber, copyKey: "cbe" },
    ];

  const manualSteps = isTelebirr
    ? [
      <>Send exactly <strong>{amount} ETB</strong> to the Telebirr phone number above.</>,
      <>Copy the <strong>Transaction ID</strong> from the confirmation SMS you receive.</>,
      <>Paste the transaction ID below and click <strong>Verify payment</strong>.</>,
    ]
    : [
      <>Transfer exactly <strong>{amount} ETB</strong> to the CBE account number above.</>,
      <>Copy the <strong>official receipt link</strong> from the CBE confirmation SMS.</>,
      <>Paste the receipt link below and click <strong>Verify payment</strong>.</>,
    ];

  const manualInputValue = isTelebirr ? txnId : cbeReceiptLink;
  const manualBusy = isTelebirr ? verifyingManual : verifyingCbe;
  const isSubmitting = loading || altLoading || verifyingManual || verifyingCbe || cancellingManual;

  function copyToClipboard(text: string, fieldKey: string) {
    if (!text) return;
    navigator.clipboard.writeText(text);
    setCopiedField(fieldKey);
    setTimeout(() => setCopiedField(null), 2000);
  }

  // Cancel pending payment request (supports both manual and Chapa)
  async function handleCancelPayment() {
    if (!token || !pendingPayment) return;
    setCancellingManual(true);
    try {
      await api.payments.cancelPayment(token, pendingPayment.txRef || pendingPayment.id);
      setPendingPayment(null);
      setMessage("Payment request was cancelled. You can now select a package and submit a new request.");
      setMessageTone("info");
    } catch (err) {
      setMessage(err instanceof ApiError ? mapApiError(err).message : "We could not cancel this payment request. Please try again.");
      setMessageTone("error");
    } finally {
      setCancellingManual(false);
    }
  }

  // Handle successful upgrade activation
  async function handleUpgradeSuccess(msg: string) {
    setMessageTone("success");
    setMessage(msg);
    setIsPremium(true);
    setPaymentSuccess(true);
    invalidateStudySubjectsCache();

    if (token) {
      try {
        const [ents, semStats] = await Promise.all([
          api.payments.entitlements(token),
          api.payments.semesterStatus(token),
        ]);
        setEntitlements(ents);
        setSemesterStatuses(semStats);
      } catch {
        // Ignored
      }
    }
  }

  // Telebirr manual submit
  async function handleTelebirrVerify(e: React.FormEvent) {
    e.preventDefault();
    const cleanId = txnId.trim().toUpperCase();
    if (!cleanId || !token || !hasProfileStream) return;

    setVerifyingManual(true);
    setMessage("");
    setMessageTone("info");

    try {
      const res = await api.payments.verifyTelebirrManual(
        token,
        cleanId,
        plan,
        selectedPackage?.id,
        semQuery || 1,
        idempotencyKey,
      );
      if (res.status === "pending" || res.status === "pending_review") {
        setSubmittedReviewInfo({
          txnId: cleanId,
          plan: selectedPackage?.name || plan,
          amount: res.amountEtb || amount,
        });
        setPendingPayment({
          id: res.txRef || `TELEMAN-${cleanId}`,
          txRef: res.txRef || `TELEMAN-${cleanId}`,
          txnId: cleanId,
          plan: selectedPackage?.name || plan,
          amount: res.amountEtb || amount,
          amountEtb: res.amountEtb || amount,
          date: new Date().toISOString(),
          status: res.status,
          packageName: selectedPackage?.name,
          canCancel: true,
          provider: "telebirr",
        });
        setTxnId("");
      } else {
        await handleUpgradeSuccess(res.message || "Payment verified! Premium access is now active.");
        setTxnId("");
        setPendingPayment(null);
      }
    } catch (err) {
      setMessageTone("error");
      const errorMsg =
        err instanceof ApiError
          ? mapApiError(err).message
          : "We couldn't verify your payment right now. Please check your transaction ID or try again shortly.";
      setMessage(errorMsg);
    } finally {
      setVerifyingManual(false);
    }
  }

  // CBE manual submit
  async function handleCbeVerify(e: React.FormEvent) {
    e.preventDefault();
    const cleanLink = cbeReceiptLink.trim();
    if (!cleanLink || !token || !hasProfileStream) return;

    setVerifyingCbe(true);
    setMessage("");
    setMessageTone("info");

    try {
      const res = await api.payments.verifyCbeManual(
        token,
        cleanLink,
        plan,
        selectedPackage?.id,
        semQuery || 1,
        idempotencyKey,
      );
      if (res.status === "pending" || res.status === "pending_review") {
        setSubmittedReviewInfo({
          txnId: cleanLink,
          plan: selectedPackage?.name || plan,
          amount: res.amountEtb || amount,
        });
        setPendingPayment({
          id: res.txRef || `CBE-${cleanLink}`,
          txRef: res.txRef || `CBE-${cleanLink}`,
          txnId: cleanLink,
          plan: selectedPackage?.name || plan,
          amount: res.amountEtb || amount,
          amountEtb: res.amountEtb || amount,
          date: new Date().toISOString(),
          status: res.status,
          packageName: selectedPackage?.name,
          canCancel: true,
          provider: "manual",
        });
        setCbeReceiptLink("");
      } else {
        await handleUpgradeSuccess(res.message || "Payment verified! Premium access is now active.");
        setCbeReceiptLink("");
        setPendingPayment(null);
      }
    } catch (err) {
      setMessageTone("error");
      const errorMsg =
        err instanceof ApiError
          ? mapApiError(err).message
          : "We couldn't verify your receipt link right now. Please verify the URL and try again shortly.";
      setMessage(errorMsg);
    } finally {
      setVerifyingCbe(false);
    }
  }

  // Online gateway checkout (Chapa / Telebirr gateway)
  async function pay(provider?: "chapa" | "telebirr") {
    if (!token || !hasProfileStream) return;
    if (provider) setAltLoading(true);
    else setLoading(true);
    setMessage("");
    setMessageTone("info");

    try {
      const result = await api.payments.initiate(
        token,
        plan,
        provider,
        selectedPackage?.id,
        semQuery || 1,
        idempotencyKey,
      );
      if (result.checkoutUrl) {
        window.location.href = result.checkoutUrl;
        return;
      }
      if (
        (result as { upgraded?: boolean; status?: string }).upgraded ||
        (result as { status?: string }).status === "success"
      ) {
        await handleUpgradeSuccess("Payment confirmed! You're now on Premium.");
      } else {
        setMessageTone("info");
        setMessage(result.message || "Payment initiated.");
      }
    } catch (e) {
      setMessageTone("error");
      setMessage(e instanceof ApiError ? mapApiError(e).message : "We could not start this payment. Please try again or choose Bank Transfer.");
    } finally {
      setLoading(false);
      setAltLoading(false);
    }
  }

  // Active status for the selected semester
  const activeSemesterEntitlement = useMemo(() => {
    if (selectedSemesterTab === "bundle") return null;
    return entitlements.find((e) => e.semester === selectedSemesterTab);
  }, [entitlements, selectedSemesterTab]);

  const activeSemesterStatus = useMemo(() => {
    if (selectedSemesterTab === "bundle") return null;
    return semesterStatuses.find((s) => s.semester === selectedSemesterTab);
  }, [semesterStatuses, selectedSemesterTab]);

  return (
    <div className="max-w-4xl mx-auto space-y-6 pb-12 animate-in fade-in duration-200">
      {/* Top back navigation */}
      <div className="flex items-center justify-between">
        {currentStep === 1 ? (
          <button
            type="button"
            onClick={leavePremium}
            className="inline-flex items-center gap-1.5 text-xs font-benton font-semibold text-muted-foreground hover:text-foreground transition group cursor-pointer"
          >
            <ArrowLeft size={14} className="group-hover:-translate-x-0.5 transition-transform" />
            Back
          </button>
        ) : (
          <button
            type="button"
            onClick={backToPackages}
            className="inline-flex items-center gap-1.5 text-xs font-benton font-semibold text-muted-foreground hover:text-foreground transition group cursor-pointer"
          >
            <ArrowLeft size={14} className="group-hover:-translate-x-0.5 transition-transform" />
            Back to Packages
          </button>
        )}

        {typeof selectedSemesterTab === "number" && (
          <Link
            to={`/student/study/semester/${selectedSemesterTab}`}
            className="text-xs font-benton font-semibold text-primary hover:underline inline-flex items-center gap-1"
          >
            <span>View Semester {selectedSemesterTab} Courses</span>
            &rarr;
          </Link>
        )}
      </div>

      {/* Header */}
      <div className="space-y-1">
        <h1 className="text-2xl sm:text-3xl font-bold font-display text-foreground tracking-tight">Premium</h1>
        <p className="text-sm text-muted-foreground">Open every chapter, quiz and past exam for your semester.</p>
      </div>

      {/* Active Entitlements Banner */}
      {entitlements.length > 0 && (
        <div className="bg-success/10 border border-success/30 rounded-xs p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-full bg-success/20 text-success flex items-center justify-center shrink-0 mt-0.5">
              <CheckCircle2 size={18} />
            </div>
            <div>
              <p className="text-xs font-bold text-success uppercase tracking-wider">Your access</p>
              <div className="flex flex-wrap gap-2 mt-1">
                {entitlements.map((ent, idx) => (
                  <span
                    key={idx}
                    className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-xs bg-card border border-success/30 text-foreground"
                  >
                    <GraduationCap size={12} className="text-success" />
                    Semester {ent.semester} ({ent.stream})
                    {ent.expires_at && (
                      <span className="text-muted-foreground font-normal text-xs">
                        &middot; expires {new Date(ent.expires_at).toLocaleDateString()}
                      </span>
                    )}
                  </span>
                ))}
              </div>
            </div>
          </div>
          {typeof selectedSemesterTab === "number" && (
            <Link
              to={`/student/study/semester/${selectedSemesterTab}`}
              className="btn btn-secondary text-xs py-1.5 px-3 self-start sm:self-center"
            >
              Open Semester {selectedSemesterTab}
            </Link>
          )}
        </div>
      )}

      {/* Step Progress Tracker (2-step unified flow) */}
      <div className="rounded-card border border-border p-2 sm:p-3 bg-card shadow-soft">
        <div className="grid grid-cols-2 gap-2 relative">
          {[
            {
              step: 1 as const,
              title: "1. Choose a plan",
              desc: selectedPackage
                ? `${selectedPackage.name} (${selectedSemesterTab === "bundle" ? "Bundle" : `Sem ${selectedSemesterTab}`})`
                : "Semester and plan",
            },
            {
              step: 2 as const,
              title: "2. Pay",
              desc: `${amount} ETB`,
            },
          ].map((item) => {
            const isCurrent = currentStep === item.step;
            const isCompleted = currentStep > item.step;
            const canClick = item.step < currentStep || (item.step === 2 && canProceed);

            return (
              <button
                key={item.step}
                type="button"
                disabled={!canClick && !isCurrent}
                onClick={() => canClick && (item.step === 1 ? backToPackages() : goToStep(item.step))}
                className={`flex items-center gap-2 sm:gap-3 p-2 sm:p-2.5 rounded-key text-left transition ${
                  isCurrent
                    ? "bg-primary/10 text-primary border border-primary/30 shadow-xs"
                    : isCompleted
                    ? "text-foreground hover:bg-secondary/60 cursor-pointer border border-transparent"
                    : "text-muted-foreground opacity-50 cursor-not-allowed border border-transparent"
                }`}
              >
                <div
                  className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold shrink-0 transition ${
                    isCurrent
                      ? "bg-primary text-on-accent shadow-xs"
                      : isCompleted
                      ? "bg-success text-white"
                      : "bg-secondary text-muted-foreground border border-border"
                  }`}
                >
                  {isCompleted ? <Check size={14} strokeWidth={3} /> : item.step}
                </div>
                <div className="min-w-0">
                  <p className="text-xs font-bold truncate leading-tight font-display">{item.title}</p>
                  <p className="text-xs text-muted-foreground truncate hidden sm:block">{item.desc}</p>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* STEP 1: MERGED SEMESTER & PACKAGE SELECTION */}
      {currentStep === 1 && (
        <div className="space-y-5 animate-in fade-in duration-200">
          <div className="rounded-card border border-border p-5 sm:p-6 space-y-5 bg-card shadow-soft">
            {/* Admin Preview Stream Selector or Student Stream Badge */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-border/40 pb-4">
              <div>
                <h2 className="text-sm font-bold uppercase tracking-wider text-foreground font-display">
                  Choose a plan
                </h2>
                
              </div>

              {isAdminPreview ? (
                <div className="flex items-center gap-2 text-xs bg-primary/10 border border-primary/30 px-3 py-1.5 rounded-xs">
                  <span className="font-semibold text-primary">Admin Preview Stream:</span>
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      onClick={() => setAdminSelectedStream("natural")}
                      className={`px-2 py-0.5 rounded-xs font-semibold text-xs transition ${
                        adminSelectedStream === "natural"
                          ? "bg-primary text-on-accent shadow-xs"
                          : "bg-secondary text-foreground hover:bg-secondary/80"
                      }`}
                    >
                      Natural
                    </button>
                    <button
                      type="button"
                      onClick={() => setAdminSelectedStream("social")}
                      className={`px-2 py-0.5 rounded-xs font-semibold text-xs transition ${
                        adminSelectedStream === "social"
                          ? "bg-primary text-on-accent shadow-xs"
                          : "bg-secondary text-foreground hover:bg-secondary/80"
                      }`}
                    >
                      Social
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex items-center gap-2 text-xs shrink-0">
                  <span className="text-muted-foreground">Stream:</span>
                  {hasProfileStream ? (
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-xs bg-secondary border border-border font-semibold text-foreground">
                      <GraduationCap size={13} className="text-primary" />
                      <span>{studentProfile?.streamName || studentProfile?.streamCode}</span>
                    </span>
                  ) : (
                    <Link
                      to={`/student/settings?returnTo=${encodeURIComponent(location.pathname + location.search)}`}
                      className="text-warning font-semibold hover:underline inline-flex items-center gap-1 bg-warning/10 border border-warning/30 px-2.5 py-1 rounded-xs"
                    >
                      <AlertCircle size={13} />
                      <span>Set Stream in Settings</span>
                      &rarr;
                    </Link>
                  )}
                </div>
              )}
            </div>

            {/* Semester Selection Tabs */}
            <div className="space-y-2">
              <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground block">
                Semester
              </label>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
                {semesterTabs.map((tab) => {
                  const isSelected = selectedSemesterTab === tab.id;
                  const semStatusObj = typeof tab.id === "number" ? semesterStatuses.find((s) => s.semester === tab.id) : null;
                  const isSemUnlocked = semStatusObj?.unlocked ?? false;
                  const isSemComingSoon = semStatusObj ? (semStatusObj.status === "coming_soon" || semStatusObj.isAvailable === false) : false;
                  const isFullyPrem = Boolean(semStatusObj?.is_fully_premium || semStatusObj?.isFullyPremium);

                  return (
                    <div
                      key={tab.id}
                      onClick={() => {
                        setSelectedSemesterTab(tab.id);
                        setSearchParams((prev) => {
                          const next = new URLSearchParams(prev);
                          next.set("semester", String(tab.id));
                          return next;
                        });
                      }}
                      className={`p-3.5 rounded-tile border text-left transition relative cursor-pointer flex flex-col justify-between ${
                        isSelected
                          ? "bg-card border-primary ring-2 ring-primary/20 shadow-xs"
                          : "bg-secondary/30 hover:bg-secondary/60 text-foreground border-border"
                      }`}
                    >
                      <div>
                        <div className="flex items-center justify-between gap-1 mb-1">
                          <span className="font-bold text-sm font-display">{tab.label}</span>
                          <div className="flex items-center gap-1">
                            {isFullyPrem && (
                              <span className="text-xs font-bold px-1.5 py-0.5 rounded-xs bg-warning/15 text-warning border border-warning/30 flex items-center gap-0.5">
                                <Star size={9} fill="currentColor" />
                                Premium
                              </span>
                            )}
                            {isSemUnlocked ? (
                              <span className="text-xs font-semibold px-1.5 py-0.5 rounded-xs bg-success/15 text-success border border-success/30">
                                Unlocked
                              </span>
                            ) : isSemComingSoon ? (
                              <span className="text-xs font-semibold px-1.5 py-0.5 rounded-xs bg-secondary text-muted-foreground border border-border">
                                Coming Soon
                              </span>
                            ) : null}
                          </div>
                        </div>
                        <p className="text-xs font-semibold text-primary">{tab.subtext}</p>
                        
                      </div>

                      <div className="pt-2.5 mt-2.5 border-t border-border/40 flex items-center justify-between">
                        <span className="text-xs font-medium text-muted-foreground">
                          {isSelected ? "Selected" : "Tap to select"}
                        </span>
                        <div
                          className={`w-3.5 h-3.5 rounded-full border flex items-center justify-center shrink-0 ${
                            isSelected ? "border-primary bg-primary text-on-accent" : "border-border bg-secondary"
                          }`}
                        >
                          {isSelected && <Check size={8} strokeWidth={3} />}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Packages Grid for the Selected Semester */}
            <div className="space-y-3 pt-2">
              <div className="flex items-center justify-between">
                <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground block">
                  Plan for {selectedSemesterTab === "bundle" ? "Full Year Bundle" : `Semester ${selectedSemesterTab}`}
                </label>
                
              </div>

              {loadingData ? (
                <div className="p-8 text-center border border-border rounded-xs">
                  <Loader2 size={24} className="animate-spin text-primary mx-auto mb-2" />
                  <p className="text-xs text-muted-foreground">Loading available packages…</p>
                </div>
              ) : semesterComingSoon ? (
                <div className="rounded-card p-6 text-center border border-border bg-secondary/30 space-y-2">
                  <p className="text-sm font-bold text-foreground font-display">Semester {selectedSemesterTab} is Coming Soon</p>
                  <p className="text-xs text-muted-foreground max-w-md mx-auto">
                    Plans open when the course materials are ready.
                  </p>
                </div>
              ) : (!hasProfileStream && !isAdminPreview) ? (
                <div className="rounded-card p-6 text-center border border-warning/30 bg-warning/5 space-y-3">
                  <div className="w-10 h-10 rounded-full bg-warning/15 text-warning flex items-center justify-center mx-auto">
                    <AlertCircle size={20} />
                  </div>
                  <div className="max-w-md mx-auto space-y-1">
                    <p className="text-sm font-bold text-foreground font-display">Set your stream</p>
                    <p className="text-xs text-muted-foreground leading-relaxed">
                      Plans depend on your stream. Set it in Settings to see them.
                    </p>
                  </div>
                  <Link
                    to={`/student/settings?returnTo=${encodeURIComponent(location.pathname + location.search)}`}
                    className="btn btn-primary text-xs py-2 px-4 inline-flex items-center gap-1.5 shadow-xs"
                  >
                    <span>Set Stream in Settings</span>
                    <ArrowRight size={13} />
                  </Link>
                </div>
              ) : filteredPackages.length === 0 ? (
                <div className="rounded-card p-6 text-center border border-border space-y-2 bg-secondary/20">
                  <p className="text-sm font-semibold text-foreground">
                    {selectedSemesterTab === "bundle" ? "No packages available for the full-year bundle" : "No packages available for this semester"}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Try another semester.
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                  {filteredPackages.map((pkg) => {
                    const isSelected = selectedPackage?.id === pkg.id;

                    return (
                      <div
                        key={pkg.id}
                        onClick={() => {
                          setSelectedPackageId(pkg.id);
                          setSearchParams(
                            (prev) => {
                              const next = new URLSearchParams(prev);
                              next.set("package", pkg.id);
                              return next;
                            },
                            { replace: true },
                          );
                        }}
                        className={`cursor-pointer rounded-tile border p-4 transition relative flex flex-col justify-between ${
                          isSelected
                            ? "bg-card border-primary ring-2 ring-primary/20 shadow-sm"
                            : "bg-card border-border hover:border-primary/50"
                        }`}
                      >
                        {(pkg.discount_badge || pkg.badge_text) && (
                          <span className="absolute -top-2.5 right-3 bg-primary text-on-accent text-xs font-bold px-2 py-0.5 rounded-full shadow-xs">
                            {pkg.discount_badge || pkg.badge_text}
                          </span>
                        )}

                        <div className="space-y-2">
                          <div className="flex items-start justify-between gap-2">
                            <div>
                              <h3 className="font-bold text-sm text-foreground font-display">{pkg.name}</h3>
                              <p className="text-xs text-muted-foreground">
                                {pkg.plan_type === "monthly" ? "Monthly access" : `${pkg.duration_months} months access`}
                              </p>
                            </div>
                            <div
                              className={`w-4 h-4 rounded-full border flex items-center justify-center shrink-0 mt-0.5 ${
                                isSelected ? "border-primary bg-primary text-on-accent" : "border-border bg-secondary"
                              }`}
                            >
                              {isSelected && <Check size={10} strokeWidth={3} />}
                            </div>
                          </div>

                          <div className="pt-2 border-t border-border/40">
                            <div className="flex items-baseline gap-1.5 flex-wrap">
                              {pkg.is_discounted && pkg.original_price && pkg.original_price > pkg.price && (
                                <span className="line-through text-xs text-muted-foreground font-medium">
                                  {pkg.original_price} ETB
                                </span>
                              )}
                              <span className="text-2xl font-bold font-display text-foreground">{pkg.price}</span>
                              <span className="text-xs text-muted-foreground font-semibold">ETB</span>
                              {pkg.is_discounted && (
                                <span className="text-xs font-bold px-1.5 py-0.5 rounded-full bg-success/15 text-success border border-success/30 ml-auto">
                                  DISCOUNT
                                </span>
                              )}
                            </div>
                            {pkg.description && (
                              <p className="text-xs text-muted-foreground mt-1 line-clamp-2 leading-relaxed">
                                {pkg.description}
                              </p>
                            )}
                          </div>

                          {pkg.features && pkg.features.length > 0 && (
                            <div className="space-y-1.5 pt-2 border-t border-border/40">
                              {pkg.features.slice(0, 3).map((f, i) => (
                                <div key={i} className="flex items-center gap-1.5 text-xs text-muted-foreground">
                                  <Check size={11} className="text-primary shrink-0" />
                                  <span className="line-clamp-1">{f}</span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>

                        <div className="pt-3 mt-3 border-t border-border/40">
                          <button
                            type="button"
                            className={`w-full py-1.5 rounded-xs text-xs font-semibold transition ${
                              isSelected
                                ? "bg-primary text-on-accent shadow-xs"
                                : "bg-secondary text-foreground hover:bg-secondary/80"
                            }`}
                          >
                            {isSelected ? "Selected" : "Select package"}
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Bottom Actions for Merged Step 1 */}
            <div className="flex flex-col sm:flex-row items-center justify-between gap-3 pt-4 border-t border-border/40">
              {activeSemesterEntitlement ? (
                <div className="flex items-center gap-2 text-xs text-success">
                  <CheckCircle2 size={16} />
                  <span>You have active access to Semester {selectedSemesterTab}.</span>
                  {typeof selectedSemesterTab === "number" && (
                    <Link
                      to={`/student/study/semester/${selectedSemesterTab}`}
                      className="text-primary font-semibold hover:underline ml-1"
                    >
                      Open courses &rarr;
                    </Link>
                  )}
                </div>
              ) : selectedPackage ? (
                <div className="text-xs text-muted-foreground">
                  Selected: <strong className="text-foreground">{selectedPackage.name}</strong> ({amount} ETB) for{" "}
                  <strong>{selectedSemesterTab === "bundle" ? "Full Year Bundle" : `Semester ${selectedSemesterTab}`}</strong>.
                </div>
              ) : (
                <div className="text-xs text-muted-foreground">
                  {filteredPackages.length === 0 && !loadingData ? "No plan for this choice." : "Choose a plan to continue."}
                </div>
              )}

              <div className="flex items-center gap-2 w-full sm:w-auto">
                <button
                  type="button"
                  onClick={() => goToStep(2)}
                  disabled={!canProceed}
                  className="btn btn-primary text-xs py-2 px-5 flex-1 sm:flex-initial inline-flex items-center justify-center gap-1.5 shadow-xs font-semibold disabled:opacity-50"
                >
                  <span>Continue ({amount} ETB)</span>
                  <ArrowRight size={14} />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* STEP 2: FAST PAYMENT & ACTIVATION */}
      {currentStep === 2 && (
        <div className="space-y-4 animate-in fade-in duration-200">
          {/* Order Summary Pill */}
          <div className="rounded-tile bg-soft border border-border p-3.5 sm:p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-primary/10 text-primary flex items-center justify-center shrink-0">
                <Star size={16} />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h3 className="text-sm font-bold font-display text-foreground">
                    {selectedPackage?.name || "Semester Package"}
                  </h3>
                  <span className="text-xs font-semibold px-2 py-0.2 rounded-xs bg-secondary border border-border text-muted-foreground">
                    {selectedSemesterTab === "bundle" ? "Semesters 1 & 2" : `Semester ${selectedSemesterTab}`}
                  </span>
                </div>
                <p className="text-xs text-muted-foreground">
                  Stream: {studentProfile?.streamName || (isAdminPreview ? `${adminSelectedStream} (preview)` : "Standard")} &middot; Total: <strong className="text-foreground">{amount} ETB</strong>
                </p>
              </div>
            </div>

            <button
              type="button"
              onClick={() => goToStep(1)}
              className="text-xs font-benton font-semibold text-primary hover:underline inline-flex items-center gap-1 self-start sm:self-center"
            >
              <ArrowLeft size={12} />
              <span>Change plan</span>
            </button>
          </div>

          {/* Payment Processing Section */}
          <div className="rounded-card border border-border p-5 sm:p-6 space-y-4 bg-card shadow-soft">
            <h2 className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
              Pay {amount} ETB
            </h2>

            {paymentSuccess ? (
              <div className="py-8 px-4 text-center space-y-3 animate-in fade-in duration-200">
                <div className="w-12 h-12 rounded-full bg-success/10 text-success flex items-center justify-center mx-auto border border-success/30">
                  <CheckCircle2 size={28} />
                </div>
                <h3 className="text-xl font-bold font-display text-foreground">
                  Premium is active
                </h3>
                <p className="text-sm text-muted-foreground max-w-sm mx-auto leading-relaxed">
                  Everything in your plan is now open.
                </p>
                <div className="pt-3 flex items-center justify-center gap-3">
                  {typeof selectedSemesterTab === "number" ? (
                    <Link
                      to={`/student/study/semester/${selectedSemesterTab}`}
                      className="btn btn-primary text-xs py-2 px-4 shadow-xs"
                    >
                      Go to Semester {selectedSemesterTab} Courses &rarr;
                    </Link>
                  ) : (
                    <Link to="/student/study" className="btn btn-primary text-xs py-2 px-4 shadow-xs">
                      Go to Study Hub &rarr;
                    </Link>
                  )}
                </div>
              </div>
            ) : submittedReviewInfo ? (
              <div className="py-8 px-4 text-center space-y-3 animate-in fade-in duration-200">
                <div className="w-12 h-12 rounded-full bg-success/10 text-success flex items-center justify-center mx-auto border border-success/30">
                  <CheckCircle2 size={28} />
                </div>
                <h3 className="text-xl font-bold font-display text-foreground">
                  Payment submitted for review
                </h3>
                <p className="text-sm text-muted-foreground max-w-sm mx-auto leading-relaxed">
                  We received your transaction reference{" "}
                  <span className="font-mono font-semibold text-foreground select-all">{submittedReviewInfo.txnId}</span>. We will open your access after review.</p>
                <div className="pt-3">
                  <button
                    type="button"
                    onClick={() => setSubmittedReviewInfo(null)}
                    className="px-5 py-2 rounded-xs bg-primary text-on-accent text-xs font-semibold hover:bg-primary/90 transition shadow-xs"
                  >
                    Done
                  </button>
                </div>
              </div>
            ) : typeof selectedSemesterTab === "number" && semesterStatuses.some((s) => s.semester === selectedSemesterTab && (s.status === "coming_soon" || s.isAvailable === false)) ? (
              <div className="rounded-card p-6 text-center border border-border bg-secondary/30 space-y-3 max-w-lg mx-auto">
                <div className="w-10 h-10 rounded-full bg-secondary text-muted-foreground flex items-center justify-center mx-auto">
                  <Clock size={20} />
                </div>
                <div className="space-y-1">
                  <h3 className="text-sm font-bold text-foreground font-display">Semester {selectedSemesterTab} Coming Soon</h3>
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    Courses and learning resources for Semester {selectedSemesterTab} are currently being prepared. Subscriptions for this semester will open as soon as courses go live!
                  </p>
                </div>
              </div>
            ) : !hasProfileStream ? (
              <div className="rounded-card p-6 text-center border border-warning/30 bg-warning/5 space-y-3 max-w-lg mx-auto">
                <div className="w-10 h-10 rounded-full bg-warning/15 text-warning flex items-center justify-center mx-auto">
                  <AlertCircle size={20} />
                </div>
                <div className="space-y-1">
                  <h3 className="text-sm font-bold text-foreground font-display">Set your stream</h3>
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    You must set your academic stream (Natural Science or Social Science) in settings before purchasing premium access.
                  </p>
                </div>
                <Link
                  to={`/student/settings?returnTo=${encodeURIComponent(location.pathname + location.search)}`}
                  className="btn btn-primary text-xs py-2 px-4 inline-flex items-center gap-1.5 shadow-xs"
                >
                  <span>Set Stream in Settings</span>
                  <ArrowRight size={13} />
                </Link>
              </div>
            ) : pendingPayment ? (
              <div className="bg-warning/10 border border-warning/30 rounded-xs p-5 space-y-4 max-w-xl">
                <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
                  <div className="space-y-1">
                    <div className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-warning/20 text-warning text-xs font-bold">
                      <Clock size={12} />
                      <span>Awaiting review</span>
                    </div>
                    <h3 className="text-base font-bold text-foreground font-display">
                      {pendingPayment.packageName || (pendingPayment.plan === "monthly" ? "Monthly Access" : "Semester Access")}
                    </h3>
                    <p className="text-xs text-muted-foreground">
                      Reference:{" "}
                      <span className="font-mono font-semibold text-foreground select-all">
                        {pendingPayment.txnId || pendingPayment.txRef}
                      </span>
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Method: <span className="font-semibold text-foreground capitalize">{pendingPayment.isChapa ? "Chapa Online" : (pendingPayment.txRef?.startsWith("CBEMAN") || pendingPayment.provider === "cbe") ? "CBE Bank Transfer" : "Telebirr"}</span>
                      {pendingPayment.semester ? ` · Semester ${pendingPayment.semester}` : ""}
                    </p>
                  </div>

                  <div className="text-left sm:text-right shrink-0">
                    <div className="text-xl font-bold font-display text-foreground">
                      {pendingPayment.amountEtb || pendingPayment.amount} ETB
                    </div>
                    {pendingPayment.date && (
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {new Date(pendingPayment.date).toLocaleDateString(undefined, {
                          month: "short",
                          day: "numeric",
                          year: "numeric",
                        })}
                      </p>
                    )}
                  </div>
                </div>

                <div className="text-xs text-muted-foreground/90 leading-relaxed bg-card/60 p-3 rounded-xs border border-border/50">
                  Your payment is being reviewed. You can send another once it is done.
                </div>

                {message && (
                  <StatusBanner
                    type={messageTone === "error" ? "error" : messageTone === "success" ? "success" : "info"}
                    message={message}
                  />
                )}

                <div className="flex flex-wrap items-center gap-2 pt-1">
                  {pendingPayment.canCancel && (
                    <button
                      type="button"
                      onClick={handleCancelPayment}
                      disabled={isSubmitting}
                      className="btn btn-secondary text-xs py-1.5 px-3 text-destructive hover:bg-destructive/10 border-border disabled:opacity-50 inline-flex items-center gap-1.5"
                    >
                      {cancellingManual && <Loader2 size={12} className="animate-spin" />}
                      <span>Cancel request</span>
                    </button>
                  )}

                  {pendingPayment.isChapa && pendingPayment.checkoutUrl && (
                    <a
                      href={pendingPayment.checkoutUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn btn-primary text-xs py-1.5 px-3 inline-flex items-center gap-1.5 shadow-xs"
                    >
                      <span>Continue Chapa checkout</span>
                      <ExternalLink size={12} />
                    </a>
                  )}
                </div>
              </div>
            ) : (
              <>
                {/* Payment Mode Selector: Bank transfer vs Online checkout */}
                {manualAvailable && onlineAvailable && (
                  <div className="grid grid-cols-2 gap-1 rounded-xs p-1 bg-secondary/80 border border-border max-w-md">
                    {(
                      [
                        {
                          id: "manual",
                          label: "Bank transfer",
                          icon: Smartphone,
                        },
                        {
                          id: "online",
                          label: "Pay online",
                          icon: ShieldCheck,
                        },
                      ] as const
                    ).map(({ id, label, icon: Icon }) => (
                      <button
                        key={id}
                        type="button"
                        onClick={() => {
                          setPaymentMode(id);
                          setMessage("");
                        }}
                        className={`py-2 px-3 rounded-xs text-xs font-benton font-semibold flex items-center justify-center gap-1.5 transition ${paymentMode === id
                            ? "bg-card text-primary shadow-xs border border-border"
                            : "text-muted-foreground hover:text-foreground"
                          }`}
                      >
                        <Icon size={13} />
                        {label}
                      </button>
                    ))}
                  </div>
                )}

                {paymentMode === "manual" && manualAvailable ? (
                  <div className="space-y-4 max-w-xl">
                    {/* Telebirr / CBE switch */}
                    {telebirrManualAvailable && cbeManualAvailable && (
                      <div className="grid grid-cols-2 gap-1 p-1 bg-secondary/60 rounded-xs border border-border max-w-sm">
                        {(
                          [
                            {
                              id: "telebirr",
                              label: "Telebirr",
                              icon: Smartphone,
                            },
                            {
                              id: "cbe",
                              label: "CBE / Bank",
                              icon: Building2,
                            },
                          ] as const
                        ).map(({ id, label, icon: Icon }) => (
                          <button
                            key={id}
                            type="button"
                            onClick={() => {
                              setManualMethod(id);
                              setMessage("");
                            }}
                            className={`flex items-center justify-center gap-1.5 py-2 px-3 rounded-xs text-xs font-benton font-bold transition ${manualMethod === id
                                ? "bg-card text-primary shadow-xs border border-border"
                                : "text-muted-foreground hover:text-foreground"
                              }`}
                          >
                            <Icon size={13} />
                            {label}
                          </button>
                        ))}
                      </div>
                    )}

                    {manualReady && (
                      <div className="space-y-4">
                        {/* Payment details box */}
                        <div className="rounded-tile bg-soft p-4 space-y-3 border border-border">
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            {manualDetails.map((d) => (
                              <div
                                key={d.label}
                                className="bg-card p-2.5 rounded-xs border border-border min-w-0"
                              >
                                <p className="text-muted-foreground text-xs mb-0.5">{d.label}</p>
                                <div className="flex items-center justify-between gap-1">
                                  <p
                                    className={`text-xs truncate ${d.copyKey
                                        ? "font-mono font-bold text-primary select-all"
                                        : "font-bold text-foreground"
                                      }`}
                                  >
                                    {d.value}
                                  </p>
                                  {d.copyKey && d.value && (
                                    <button
                                      type="button"
                                      onClick={() => copyToClipboard(d.value, d.copyKey)}
                                      className="text-muted-foreground hover:text-foreground p-1 transition shrink-0"
                                      title={`Copy ${d.label.toLowerCase()}`}
                                    >
                                      {copiedField === d.copyKey ? (
                                        <CheckCheck size={13} className="text-success" />
                                      ) : (
                                        <Copy size={13} />
                                      )}
                                    </button>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                          <div className="flex items-center justify-between text-xs pt-2 border-t border-border/40">
                            <span className="text-muted-foreground font-medium">Exact amount to transfer:</span>
                            <span className="font-bold text-sm text-foreground font-display">{amount} ETB</span>
                          </div>
                        </div>

                        {/* Step-by-step instructions */}
                        <ol className="list-decimal list-inside space-y-1.5 text-xs text-muted-foreground font-benton">
                          {manualSteps.map((s, i) => (
                            <li key={i}>{s}</li>
                          ))}
                        </ol>

                        {/* Verification Form */}
                        <form
                          onSubmit={isTelebirr ? handleTelebirrVerify : handleCbeVerify}
                          className="space-y-3 pt-1"
                        >
                          <div>
                            <label className="flex items-center gap-1.5 text-xs font-benton font-semibold mb-1 text-foreground">
                              {!isTelebirr && <LinkIcon size={12} className="text-primary" />}
                              {isTelebirr ? "Transaction ID" : "Receipt link"}
                            </label>
                            <input
                              type="text"
                              value={manualInputValue}
                              onChange={(e) =>
                                isTelebirr ? setTxnId(e.target.value) : setCbeReceiptLink(e.target.value)
                              }
                              placeholder={isTelebirr ? "e.g. DJ78ICJILA" : "https://mbreciept.cbe.com.et/..."}
                              required
                              className={`field text-xs font-mono py-2 rounded-xs w-full ${isTelebirr ? "uppercase tracking-wider" : ""
                                }`}
                            />
                          </div>

                          {message && (
                            <StatusBanner
                              type={messageTone === "error" ? "error" : messageTone === "success" ? "success" : "info"}
                              message={message}
                            />
                          )}

                          <button
                            type="submit"
                            disabled={isSubmitting || manualBusy || !manualInputValue.trim() || !hasProfileStream || Boolean(pendingPayment)}
                            className="btn btn-primary w-full py-2.5 rounded-xs text-xs font-semibold disabled:opacity-60 flex items-center justify-center gap-2 shadow-xs"
                          >
                            {manualBusy ? (
                              <>
                                <Loader2 size={14} className="animate-spin" />
                                Verifying payment…
                              </>
                            ) : (
                              "Verify payment & activate"
                            )}
                          </button>
                        </form>

                        {/* Telegram help link */}
                        <div className="text-center pt-1">
                          <a
                            href={cbeSupportLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs text-muted-foreground hover:text-primary inline-flex items-center gap-1 transition"
                          >
                            <span>Need help? Send receipt on Telegram</span>
                            <ExternalLink size={11} />
                          </a>
                        </div>

                        {customInstructions && (
                          <p className="bg-secondary text-foreground p-3 rounded-xs text-xs border border-border leading-relaxed">
                            {customInstructions}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                ) : onlineAvailable ? (
                  <div className="space-y-4 max-w-xl">
                    <div className="space-y-2">
                      {(selectedPackage?.features ?? []).map((f) => (
                        <div key={f} className="flex items-center gap-2.5">
                          <div className="w-5 h-5 rounded-xs bg-primary/10 flex items-center justify-center shrink-0 text-primary">
                            <Check size={12} strokeWidth={3} />
                          </div>
                          <span className="text-xs font-benton text-foreground">{f}</span>
                        </div>
                      ))}
                    </div>

                    {message && (
                      <StatusBanner
                        type={messageTone === "error" ? "error" : messageTone === "success" ? "success" : "info"}
                        message={message}
                      />
                    )}

                    {chapaAvailable && (
                      <button
                        type="button"
                        onClick={() => pay("chapa")}
                        disabled={isSubmitting || loading || !hasProfileStream || Boolean(pendingPayment)}
                        className="btn btn-primary w-full py-2.5 rounded-xs text-xs font-semibold disabled:opacity-60 flex items-center justify-center gap-2 shadow-xs"
                      >
                        {loading && <Loader2 size={14} className="animate-spin" />}
                        Pay {amount} ETB with Chapa
                      </button>
                    )}

                    {!chapaAvailable && telebirrGatewayAvailable && (
                      <button
                        type="button"
                        onClick={() => pay("telebirr")}
                        disabled={isSubmitting || altLoading || !hasProfileStream || Boolean(pendingPayment)}
                        className="btn btn-primary w-full py-2.5 rounded-xs text-xs font-semibold disabled:opacity-60 flex items-center justify-center gap-2 shadow-xs"
                      >
                        {altLoading && <Loader2 size={14} className="animate-spin" />}
                        Pay {amount} ETB with Telebirr
                      </button>
                    )}
                  </div>
                ) : (
                  <div className="rounded-card p-5 text-center space-y-2 border border-border">
                    <p className="font-semibold text-foreground text-sm">Payments are temporarily offline</p>
                    <p className="text-xs text-muted-foreground">Please check back later. If you still need help, open Help.</p>
                  </div>
                )}
              </>
            )}

            </div>

          {/* Bottom Back Button */}
          <div className="flex items-center justify-between pt-2">
            <button
              type="button"
              onClick={backToPackages}
              className="btn btn-secondary text-xs py-2 px-3.5 inline-flex items-center gap-1.5"
            >
              <ArrowLeft size={13} />
              <span>Back</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
